import { useEffect, useState } from 'react';

const neutral = { x: 0, y: 0 };
const clamp = (value, limit) => Math.max(-limit, Math.min(limit, value));

// One update per animation frame; no continuous loop or renders while idle.
export default function useAssistantGaze(launcher, disabled) {
  const [gaze, setGaze] = useState(neutral);
  useEffect(() => {
    if (disabled) { setGaze(neutral); return undefined; }
    let frame = null, idle = null, target = neutral, previousScroll = window.scrollY;
    const update = next => {
      target = next;
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => { frame = null; setGaze(target); });
    };
    const settle = delay => {
      window.clearTimeout(idle);
      idle = window.setTimeout(() => update(neutral), delay);
    };
    const pointer = event => {
      if (event.pointerType === 'touch' || document.visibilityState === 'hidden') return;
      const box = launcher.current?.getBoundingClientRect();
      if (!box) return;
      update({
        x: clamp((event.clientX - box.left - box.width / 2) / Math.max(window.innerWidth * .4, 1) * 2, 2),
        y: clamp((event.clientY - box.top - box.height / 2) / Math.max(window.innerHeight * .4, 1) * 1.4, 1.4),
      });
      settle(1400);
    };
    const scroll = () => {
      const delta = window.scrollY - previousScroll;
      previousScroll = window.scrollY;
      if (!delta || document.visibilityState === 'hidden') return;
      update({ x: target.x, y: delta > 0 ? 1.4 : -1.4 });
      settle(650);
    };
    const reset = () => { window.clearTimeout(idle); update(neutral); };
    window.addEventListener('pointermove', pointer, { passive: true });
    window.addEventListener('scroll', scroll, { passive: true });
    document.addEventListener('pointerleave', reset);
    document.addEventListener('visibilitychange', reset);
    return () => {
      window.removeEventListener('pointermove', pointer);
      window.removeEventListener('scroll', scroll);
      document.removeEventListener('pointerleave', reset);
      document.removeEventListener('visibilitychange', reset);
      window.clearTimeout(idle);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [launcher, disabled]);
  return gaze;
}
